import { Autocomplete, Box, TextField, Typography } from '@mui/material'
import { FunctionComponent, useEffect, useState } from 'react'
import { ICompanyHouseSearchResult } from '../../../shared/services/company-house/company-house.model'
import { useDebounce } from '../../../hooks/use-debounce-hook'

/**
 * Reusable async Companies House company picker. The caller supplies the search function so it can
 * be used from both the super-admin overrides dialog and the supplier form.
 */
export const CompanyHousePicker: FunctionComponent<{
  value: ICompanyHouseSearchResult | null,
  onChange: (value: ICompanyHouseSearchResult | null) => void,
  search: (term: string) => Promise<ICompanyHouseSearchResult[]>,
  label?: string,
  placeholder?: string,
  autoFocus?: boolean,
}> = ({ value, onChange, search, label = 'Companies House company', placeholder = 'Search by name...', autoFocus = false }) => {
  const [term, setTerm] = useState('')
  const [options, setOptions] = useState<ICompanyHouseSearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const debounced = useDebounce(term, 400)

  useEffect(() => {
    if (!debounced || debounced.trim().length < 2) { setOptions([]); return }
    setLoading(true)
    search(debounced.trim())
      .then(results => setOptions(results))
      .catch(() => setOptions([]))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced])

  return (
    <Autocomplete
      fullWidth
      options={options}
      loading={loading}
      filterOptions={(x) => x}
      value={value}
      isOptionEqualToValue={(o, v) => o.companyNumber === v.companyNumber}
      getOptionLabel={(o) => (o.companyName ? `${o.companyName} (${o.companyNumber})` : o.companyNumber)}
      onChange={(_, v) => onChange(v)}
      onInputChange={(_, v, reason) => { if (reason === 'input') setTerm(v) }}
      renderOption={(props, o) => (
        <li {...props} key={o.companyNumber}>
          <Box>
            <Typography variant="body2">{o.companyName}</Typography>
            <Typography variant="caption" color="text.secondary">
              {o.companyNumber}{o.sicCode ? ` · SIC ${o.sicCode}` : ''}{o.companyStatus ? ` · ${o.companyStatus}` : ''}
            </Typography>
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField {...params} size="small" label={label} placeholder={placeholder} autoFocus={autoFocus} />
      )}
    />
  )
}
